import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';
import {
  InventoryService,
  Inventory,
  InventoryCategory,
  InventoryMovement,
  PagedInventoryMovementRequest,
  PagedInventoryRequest,
} from '@services';
import { EntityDto } from '@services';

class InventoryStore {
  inventories: Map<string, Inventory> = new Map();
  inventory: Inventory | undefined;
  categories: InventoryCategory[] = [];
  skipCount: number = 0;
  currentPage: number = 1;
  totalCount: number = 0;
  loading: boolean = false;
  saving: boolean = false;
  deleting: boolean = false;
  maxResultCount: number = 15;

  movements: InventoryMovement[] = [];
  movementsTotalCount: number = 0;
  movementsLoading: boolean = false;
  movementsSkipCount: number = 0;
  movementsCurrentPage: number = 1;
  movementsMaxResultCount: number = 15;

  constructor() {
    makeAutoObservable(this);
  }

  async getAll(request: PagedInventoryRequest) {
    this.setLoading(true);
    try {
      const result = await InventoryService.getAll(request);
      runInAction(() => {
        this.inventories.clear();
        result.items.forEach((x) => this.inventories.set(x.id, x));
        this.totalCount = result.totalCount;
      });
    } catch (error) {
      console.error(error);
    }
    this.setLoading(false);
  }

  async get(entityDto: EntityDto<string>) {
    this.setLoading(true);
    try {
      const result = await InventoryService.get(entityDto);
      runInAction(() => {
        this.inventory = result;
      });
    } catch (error) {
      console.error(error);
    }
    this.setLoading(false);
  }

  async create(request: any): Promise<Inventory | null> {
    this.setSaving(true);
    try {
      const result = await InventoryService.create(request);
      runInAction(() => {
        this.inventories.set(result.id, result);
        this.totalCount += 1;
      });
      message.success({ content: 'Inventory item created successfully!' });
      return result;
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      this.setSaving(false);
    }
  }

  async update(request: any): Promise<Inventory | null> {
    this.setSaving(true);
    try {
      const result = await InventoryService.update(request);
      runInAction(() => {
        this.inventories.set(result.id, result);
        this.inventory = result;
      });
      message.success({ content: 'Inventory item updated successfully!' });
      return result;
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      this.setSaving(false);
    }
  }

  async delete(entityDto: EntityDto<string>): Promise<boolean> {
    this.setDeleting(true);
    try {
      await InventoryService.delete(entityDto);
      runInAction(() => {
        this.inventories.delete(entityDto.id);
        this.totalCount -= 1;
      });
      message.success({ content: 'Inventory item deleted successfully!' });
      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      this.setDeleting(false);
    }
  }

  async getAllCategories() {
    try {
      const result = await InventoryService.getAllCategories();
      runInAction(() => {
        this.categories = result.items;
      });
    } catch (error) {
      console.error(error);
    }
  }

  async createCategory(request: any) {
    try {
      const result = await InventoryService.createCategory(request);
      runInAction(() => {
        this.categories = [...this.categories, result];
      });
      message.success({ content: 'Category created successfully!' });
    } catch (error) {
      console.error(error);
    }
  }

  async deleteCategory(entityDto: EntityDto<string>) {
    try {
      await InventoryService.deleteCategory(entityDto);
      runInAction(() => {
        this.categories = this.categories.filter((x) => x.id !== entityDto.id);
      });
      message.success({ content: 'Category deleted successfully!' });
    } catch (error) {
      console.error(error);
    }
  }

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  setSaving = (value: boolean) => {
    runInAction(() => {
      this.saving = value;
    });
  };

  setDeleting = (value: boolean) => {
    runInAction(() => {
      this.deleting = value;
    });
  };

  handlePageChange = (pagination: any) => {
    runInAction(() => {
      this.maxResultCount = pagination.pageSize;
      this.skipCount = (pagination.current - 1) * pagination.pageSize;
      this.currentPage = pagination.current;
    });
  };

  setSkipCount = (value: number) => {
    runInAction(() => {
      this.skipCount = value;
    });
  };

  setCurrentPage = (value: number) => {
    runInAction(() => {
      this.currentPage = value;
    });
  };

  async getAllMovements(request: PagedInventoryMovementRequest) {
    runInAction(() => { this.movementsLoading = true; });
    try {
      const result = await InventoryService.getAllMovements(request);
      runInAction(() => {
        this.movements = result.items;
        this.movementsTotalCount = result.totalCount;
      });
    } catch (error) {
      console.error(error);
    }
    runInAction(() => { this.movementsLoading = false; });
  }

  handleMovementsPageChange = (pagination: any) => {
    runInAction(() => {
      this.movementsMaxResultCount = pagination.pageSize;
      this.movementsSkipCount = (pagination.current - 1) * pagination.pageSize;
      this.movementsCurrentPage = pagination.current;
    });
  };

  setMovementsSkipCount = (value: number) => {
    runInAction(() => { this.movementsSkipCount = value; });
  };

  setMovementsCurrentPage = (value: number) => {
    runInAction(() => { this.movementsCurrentPage = value; });
  };
}

export default InventoryStore;
